export type ReportChunkArgs = {
  key?: string
  chunk?: string
  index?: number
  done?: boolean
  interrupted?: true
  flush?: boolean
}

export type ChunkFlushArgs = {
  key: string
  chunk: string
  content: string
  done: boolean
  interrupted?: true
}

export default function createChunkBuffer(
  initialBufferStates: Record<string, ChunkBufferState>,
  onFlush: (args: ChunkFlushArgs) => void
) {
  let bufferStates: Record<string, ChunkBufferState> = { ...initialBufferStates }

  function reportChunk(args: ReportChunkArgs) {
    const { key, chunk, index, done = false, interrupted, flush } = args

    if (!key) {
      const knownKeys = Object.keys(bufferStates)
      for (const key of knownKeys) reportChunk({ ...args, key })
      return
    }

    if (!bufferStates[key]) bufferStates[key] = initialChunkBufferState()
    const state = bufferStates[key]

    state.timer && clearTimeout(state.timer)

    if (typeof chunk === 'string' && typeof index === 'number') {
      state.buffer.push({ chunk, index })

      if (!done) {
        state.timer = setTimeout(() => reportChunk({ key, flush: true }), MESSAGE_BUFFER_TIMEOUT)
      }
    }

    if (flush || done || interrupted || Date.now() - state.t >= MESSAGE_BUFFER_TIMEOUT) {
      const { buffer, nextChunkIndex } = extrudeAvailableBuffer(state.buffer, state.exceptedChunkIndex)
      state.t = Date.now()
      state.exceptedChunkIndex = nextChunkIndex
      if (done) state.exceptedChunkIndex = CHUNK_INDEX_START

      if (flush && !buffer) return

      state.fullContent += buffer
      onFlush({ key, chunk: buffer, content: state.fullContent, done, interrupted })

      if (done) {
        state.timer && clearTimeout(state.timer)
        delete bufferStates[key]
      }
    }
  }

  function resetBuffer() {
    Object.values(bufferStates).forEach(state => {
      state.timer && clearTimeout(state.timer)
    })
    bufferStates = { ...initialBufferStates }
  }

  return { reportChunk, resetBuffer }
}

const CHUNK_INDEX_START = 1

type ChunkBuffer = { chunk: string; index: number }[]

function extrudeAvailableBuffer(buffer: ChunkBuffer, exceptedNextChunkIndex: number) {
  if (buffer.length === 0) return { buffer: '', nextChunkIndex: exceptedNextChunkIndex }

  buffer.sort((a, b) => a.index - b.index)

  const droppedChunksCount = buffer.findIndex(chunk => chunk.index >= exceptedNextChunkIndex)
  if (droppedChunksCount > 0) buffer.splice(0, droppedChunksCount)

  let expectedIndex = exceptedNextChunkIndex
  let bufferedChunk = ''
  let remainingBufferFromIndex = 0
  for (let i = 0; i < buffer.length; i++) {
    const item = buffer[i]!
    if (expectedIndex !== item.index) break

    bufferedChunk += item.chunk
    expectedIndex++
    remainingBufferFromIndex = i + 1
  }

  buffer.splice(0, remainingBufferFromIndex)
  return { buffer: bufferedChunk, nextChunkIndex: expectedIndex }
}

type ChunkBufferState = {
  buffer: ChunkBuffer
  fullContent: string
  exceptedChunkIndex: number
  t: number
  timer?: ReturnType<typeof setTimeout>
}

const initialChunkBufferState = (): ChunkBufferState => ({
  buffer: [],
  fullContent: '',
  exceptedChunkIndex: CHUNK_INDEX_START,
  t: 0,
})

const MESSAGE_BUFFER_TIMEOUT = 50
